import { Box, Button, Container, FormField, Header, Input, SpaceBetween, Table, TableProps } from '@amzn/awsui-components-core';
import { FormikProps } from 'formik';
import React, { useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import CustomQuillEditor from 'src/components/generic-components/quill-editor';
import { ContactDetails, ContactInformation } from '../../../widgets/contacts-widget/ContactsData';
import { ContactsWidgetContent } from '../../../widgets/contacts-widget/ContactsWidget';
import { HomePageWizard } from '../HomepageModel';

interface HomePageContactsStepProps {
  formik: FormikProps<HomePageWizard>;
}

const HomePageContactsStep: React.FC<HomePageContactsStepProps> = ({ formik }) => {
  const contacts = formik.values.homePageDetailsAndWidgets.contactsWidgetContacts || [];
  const officeHoursText = formik.values.homePageDetailsAndWidgets.officeHoursText || '';

  const [selectedContacts, setSelectedContacts] = useState<ContactInformation[]>([]);
  const [editingContact, setEditingContact] = useState<ContactInformation | null>(null);
  const [isEditing, setIsEditing] = useState(false);

  // New contact form state
  const [newGroupName, setNewGroupName] = useState('');
  const [newPrimaryAlias, setNewPrimaryAlias] = useState('');
  const [newPrimaryName, setNewPrimaryName] = useState('');
  const [newOtherAliases, setNewOtherAliases] = useState('');

  const resetForm = () => {
    setNewGroupName('');
    setNewPrimaryAlias('');
    setNewPrimaryName('');
    setNewOtherAliases('');
    setEditingContact(null);
    setIsEditing(false);
  };

  const handleAddOrUpdateContact = () => {
    if (!newGroupName || !newPrimaryAlias) return;

    const otherContacts = newOtherAliases
      .split(',')
      .map((a) => a.trim())
      .filter((a) => a.length > 0)
      .map((alias) => ({ contactAlias: alias, contactName: alias }));

    const contact: ContactInformation = {
      contactId: isEditing && editingContact ? editingContact.contactId : uuidv4(),
      groupName: newGroupName,
      primaryContact: { contactAlias: newPrimaryAlias, contactName: newPrimaryName || newPrimaryAlias },
      otherContacts: otherContacts
    };

    let updatedContacts: ContactInformation[];
    if (isEditing && editingContact) {
      updatedContacts = contacts.map((c) => (c.contactId === editingContact.contactId ? contact : c));
    } else {
      updatedContacts = [...contacts, contact];
    }

    formik.setFieldValue('homePageDetailsAndWidgets.contactsWidgetContacts', updatedContacts);
    resetForm();
  };

  const handleEditContact = () => {
    if (selectedContacts.length !== 1) return;
    const contact = selectedContacts[0];
    setEditingContact(contact);
    setIsEditing(true);
    setNewGroupName(contact.groupName);
    setNewPrimaryAlias(contact.primaryContact?.contactAlias ?? '');
    setNewPrimaryName(contact.primaryContact?.contactName ?? '');
    setNewOtherAliases((contact.otherContacts ?? []).map((c: ContactDetails) => c.contactAlias).join(', '));
    setSelectedContacts([]);
  };

  const handleDeleteContacts = () => {
    const selectedIds = selectedContacts.map((c) => c.contactId);
    const updatedContacts = contacts.filter((c) => !selectedIds.includes(c.contactId));
    formik.setFieldValue('homePageDetailsAndWidgets.contactsWidgetContacts', updatedContacts);
    setSelectedContacts([]);
  };

  const handleMoveUp = () => {
    if (selectedContacts.length !== 1) return;
    const idx = contacts.findIndex((c) => c.contactId === selectedContacts[0].contactId);
    if (idx <= 0) return;
    const updated = [...contacts];
    [updated[idx - 1], updated[idx]] = [updated[idx], updated[idx - 1]];
    formik.setFieldValue('homePageDetailsAndWidgets.contactsWidgetContacts', updated);
  };

  const handleMoveDown = () => {
    if (selectedContacts.length !== 1) return;
    const idx = contacts.findIndex((c) => c.contactId === selectedContacts[0].contactId);
    if (idx < 0 || idx >= contacts.length - 1) return;
    const updated = [...contacts];
    [updated[idx], updated[idx + 1]] = [updated[idx + 1], updated[idx]];
    formik.setFieldValue('homePageDetailsAndWidgets.contactsWidgetContacts', updated);
  };

  const columnDefinitions: TableProps.ColumnDefinition<ContactInformation>[] = [
    {
      id: 'groupName',
      header: 'Group Name',
      cell: (item) => item.groupName,
      sortingField: 'groupName',
      width: 200
    },
    {
      id: 'primaryContact',
      header: 'Primary Contact',
      cell: (item) => {
        const alias = item.primaryContact?.contactAlias ?? '-';
        const name = item.primaryContact?.contactName ?? '';
        return `${alias}${name && name !== alias ? ` (${name})` : ''}`;
      },
      width: 200
    },
    {
      id: 'otherContacts',
      header: 'Other Contacts',
      cell: (item) => (item.otherContacts ?? []).map((c) => c.contactAlias).join(', ') || '-'
    }
  ];

  return (
    <SpaceBetween size="l" direction="vertical">
      {/* Office Hours Text */}
      <Container
        header={
          <Header variant="h2" description="This text appears under the 'Office Hours' heading in the Contacts widget">
            Office Hours Text
          </Header>
        }
      >
        <CustomQuillEditor
          name="homePageDetailsAndWidgets.officeHoursText"
          label="Office Hours Content"
          value={officeHoursText}
          onChange={(name, value) => {
            const cleaned = value?.replace(/(<p><br><\/p>)+$/g, '') || '';
            formik.setFieldValue(name, cleaned);
          }}
          onBlur={() => formik.setFieldTouched('homePageDetailsAndWidgets.officeHoursText', true)}
        />
      </Container>

      {/* Add/Edit Contact Form */}
      <Container
        header={
          <Header variant="h2" description="Add or edit contacts that appear under the 'Key Contacts' heading">
            {isEditing ? 'Edit Contact' : 'Add New Contact'}
          </Header>
        }
      >
        <SpaceBetween size="m" direction="vertical">
          <FormField label="Group / Role Name" description="e.g., 'DaS FinTech Lead', 'PMO Lead'">
            <Input placeholder="Enter group or role name" value={newGroupName} onChange={({ detail }) => setNewGroupName(detail.value)} />
          </FormField>
          <FormField label="Primary Contact Alias" description="The Amazon alias of the primary contact">
            <Input placeholder="e.g., johndoe" value={newPrimaryAlias} onChange={({ detail }) => setNewPrimaryAlias(detail.value)} />
          </FormField>
          <FormField
            label={
              <>
                Primary Contact Display Name<i> - optional</i>
              </>
            }
            description="Display name (defaults to alias if empty)"
          >
            <Input placeholder="e.g., John Doe" value={newPrimaryName} onChange={({ detail }) => setNewPrimaryName(detail.value)} />
          </FormField>
          <FormField
            label={
              <>
                Other Contact Aliases<i> - optional</i>
              </>
            }
            description="Comma-separated list of additional contact aliases"
          >
            <Input placeholder="e.g., janedoe, bobsmith" value={newOtherAliases} onChange={({ detail }) => setNewOtherAliases(detail.value)} />
          </FormField>
          <SpaceBetween size="s" direction="horizontal">
            <Button variant="primary" onClick={handleAddOrUpdateContact} disabled={!newGroupName || !newPrimaryAlias}>
              {isEditing ? 'Update Contact' : 'Add Contact'}
            </Button>
            {isEditing && (
              <Button variant="link" onClick={resetForm}>
                Cancel Edit
              </Button>
            )}
          </SpaceBetween>
        </SpaceBetween>
      </Container>

      {/* Contacts Table */}
      <Table
        columnDefinitions={columnDefinitions}
        items={contacts}
        selectionType="multi"
        selectedItems={selectedContacts}
        onSelectionChange={({ detail }) => setSelectedContacts(detail.selectedItems)}
        trackBy="contactId"
        empty={
          <Box textAlign="center" color="inherit">
            <b>No contacts</b>
            <Box variant="p" color="inherit">
              Use the form above to add contacts.
            </Box>
          </Box>
        }
        header={
          <Header
            variant="h2"
            counter={`(${contacts.length})`}
            actions={
              <SpaceBetween size="xs" direction="horizontal">
                <Button onClick={handleMoveUp} disabled={selectedContacts.length !== 1} iconName="angle-up">
                  Move up
                </Button>
                <Button onClick={handleMoveDown} disabled={selectedContacts.length !== 1} iconName="angle-down">
                  Move down
                </Button>
                <Button onClick={handleEditContact} disabled={selectedContacts.length !== 1}>
                  Edit
                </Button>
                <Button onClick={handleDeleteContacts} disabled={selectedContacts.length === 0}>
                  Delete
                </Button>
              </SpaceBetween>
            }
          >
            Key Contacts
          </Header>
        }
      />

      {/* Live Preview */}
      <Container header={<Header variant="h2">Preview</Header>}>
        <ContactsWidgetContent officeHoursText={officeHoursText} contacts={contacts} />
      </Container>
    </SpaceBetween>
  );
};

export default HomePageContactsStep;
